/* eslint-disable react/require-default-props, react/forbid-prop-types, no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Rocket, CheckCircle2, Circle, Trophy, TrendingUp } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { cn } from '../../../../lib/utils';

// Confetti component for current level celebration
function Confetti() {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const colors = ['#7553EA', '#F59E0B', '#10B981', '#EC4899', '#6366F1'];
    const newParticles = Array.from({ length: 10 }, (_, i) => ({
      id: i,
      color: colors[i % colors.length],
      left: `${Math.random() * 100}%`,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 2,
      size: 3 + Math.random() * 3,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-md">
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute animate-confetti"
          style={{
            left: p.left,
            top: '-6px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.size > 4 ? '50%' : '2px',
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function TimelineItem({ level, status, isLast, index, totalLevels }) {
  const isCompleted = status === 'completed';
  const isCurrent = status === 'current';
  const isFuture = status === 'future';

  return (
    <div className="relative flex gap-3">
      {/* Timeline line and dot */}
      <div className="flex flex-col items-center">
        {/* Connector line above (except for first item) */}
        {index > 0 && (
          <div
            className={cn(
              'w-0.5 h-4 -mt-4',
              isCompleted || isCurrent ? 'bg-primary' : 'bg-muted-foreground/20',
            )}
          />
        )}

        {/* Dot */}
        <div
          className={cn(
            'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-all z-10',
            isCompleted && 'border-primary bg-primary text-primary-foreground',
            isCurrent && 'border-primary bg-primary text-primary-foreground ring-2 ring-primary/20',
            isFuture && 'border-muted-foreground/30 bg-background text-muted-foreground',
          )}
        >
          {isCompleted && <CheckCircle2 className="h-4 w-4" />}
          {isCurrent && <Rocket className="h-4 w-4" />}
          {isFuture && <Circle className="h-3 w-3" />}
        </div>

        {/* Connector line below (except for last item) */}
        {!isLast && (
          <div
            className={cn(
              'w-0.5 flex-1 min-h-[32px]',
              isCompleted ? 'bg-primary' : 'bg-muted-foreground/20',
            )}
          />
        )}
      </div>

      {/* Content */}
      <div className={cn('flex-1 pb-4 pt-1', isLast && 'pb-0')}>
        <div
          className={cn(
            'relative rounded-md border px-3 py-2 transition-all min-h-[40px] flex items-center',
            isCurrent && 'border-primary bg-primary/5',
            isCompleted && 'border-muted bg-muted/30',
            isFuture && 'border-dashed',
          )}
        >
          {/* Animated confetti for current level */}
          {isCurrent && <Confetti />}
          <div className="flex items-center justify-between relative z-10 w-full">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-sm font-bold',
                  isCurrent && 'text-primary',
                  isCompleted && 'text-muted-foreground',
                  isFuture && 'text-foreground',
                )}
              >
                {level.key}
              </span>
              {index === totalLevels - 1 && <Trophy className="h-3.5 w-3.5 text-amber-500" />}
              <span
                className={cn(
                  'text-sm',
                  isCurrent && 'text-foreground font-semibold',
                  isCompleted && 'text-muted-foreground',
                  isFuture && 'text-muted-foreground',
                )}
              >
                {level.title}
              </span>
            </div>
            {isCurrent && (
              <Badge variant="default" className="text-xs bg-primary">
                Current
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

TimelineItem.propTypes = {
  level: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  status: PropTypes.oneOf(['completed', 'current', 'future']).isRequired,
  isLast: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  totalLevels: PropTypes.number.isRequired,
};

function CareerTimeline({ levels, currentLevelIndex }) {
  if (!levels.length) {
    return (
      <Card style={{ border: '1px solid hsl(var(--border))' }}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Career Path
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Rocket className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No career levels defined yet.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatus = (index) => {
    if (index < currentLevelIndex) return 'completed';
    if (index === currentLevelIndex) return 'current';
    return 'future';
  };

  // Calculate progress stats
  const completedCount = currentLevelIndex >= 0 ? currentLevelIndex : 0;
  const progressPercentage =
    levels.length > 1 ? Math.round((completedCount / (levels.length - 1)) * 100) : 0;

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Career Path
          </CardTitle>
          <Badge variant="secondary" className="text-sm">
            {progressPercentage}% Complete
          </Badge>
        </div>

        {/* Progress summary */}
        <div className="flex items-center gap-4 pt-2 text-xs">
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
            <span className="text-muted-foreground">{completedCount} done</span>
          </div>
          <div className="flex items-center gap-1">
            <Circle className="h-3.5 w-3.5 text-muted-foreground/50" />
            <span className="text-muted-foreground">
              {levels.length - completedCount - (currentLevelIndex >= 0 ? 1 : 0)} left
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-0">
          {levels.map((level, index) => (
            <TimelineItem
              key={level._id}
              level={level}
              status={getStatus(index)}
              isLast={index === levels.length - 1}
              index={index}
              totalLevels={levels.length}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

CareerTimeline.propTypes = {
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ),
  currentLevelIndex: PropTypes.number,
};

CareerTimeline.defaultProps = {
  levels: [],
  currentLevelIndex: -1,
};

export default CareerTimeline;
